import { useMutation } from '@tanstack/vue-query'
import type { AttributesFor, BodyByPath, ResponseByPath } from '@@/api/types'
import type { AdditionalVocabEntry } from '@/config/speechmaticsCustomDictionary.js'
import { useApi } from '@/composables/useApi.js'

export const SPEECHMATICS_TOKEN_TYPE = {
  REALTIME: 'rt',
  BATCH: 'batch',
} as const

export const SPEECHMATICS_REGION = {
  EUROPE: 'eu',
  UNITED_STATES: 'usa',
  AUSTRALIA: 'au',
} as const

export type SpeechmaticsTokenBody = BodyByPath<
  'post',
  '/{provet_id}/api/ai/speechmatics_token'
>
export type SpeechmaticsTokenPayload = AttributesFor<
  SpeechmaticsTokenBody['data']
>
export type SpeechmaticsTokenResponse = ResponseByPath<
  'post',
  '/{provet_id}/api/ai/speechmatics_token'
>

export function useSpeechmaticsVocabMutation() {
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: (departmentId: string) =>
      api(
        (client) =>
          client.POST('/{provet_id}/api/ai/speechmatics_vocab', {
            params: {
              path: { provet_id: route.params.provetId },
            },
            body: {
              data: {
                type: 'SpeechmaticsVocab',
                attributes: {
                  department_id: Number(departmentId),
                },
              },
            },
          }),
        (response) =>
          (
            response.data as unknown as {
              additional_vocab?: AdditionalVocabEntry[]
            }
          )?.additional_vocab ?? [],
      ),
  })
}

export function useSpeechmaticsTokenMutation() {
  const api = useApi()
  const route = useRoute()
  const config = useRuntimeConfig()

  return useMutation({
    mutationFn: (payload?: SpeechmaticsTokenPayload) => {
      const ttl = payload?.ttl ?? config.public.speechmatics.defaultTokenTtl

      return api((client) =>
        client.POST('/{provet_id}/api/ai/speechmatics_token', {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: {
            data: {
              type: 'SpeechmaticsToken',
              attributes: {
                ttl,
              },
            },
          },
        }),
      )
    },
  })
}
